import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { FounderSpotlight } from './components/FounderSpotlight';
import { SubjectOfferings } from './components/SubjectOfferings';
import { CareerMatcherModal } from './components/CareerMatcherModal';
import { BookingModal } from './components/BookingModal';
import { VideoModal } from './components/VideoModal';
import { MentorDetailModal } from './components/MentorDetailModal';
import { PrivacyPolicyModal } from './components/PrivacyPolicyModal';
import { NotFound } from './components/NotFound';
import { SmoothCursor } from './components/magicui/SmoothCursor';
import { BentoGrid, BentoCard } from './components/magicui/BentoGrid';
import { BlurFade } from './components/magicui/BlurFade';
import { ShimmerButton } from './components/magicui/ShimmerButton';
import { Users, Brain, Award, PhoneCall, ShieldCheck } from 'lucide-react';
import { trackEvent } from './lib/analytics';
import copyData from '../docs/copy/landing_page_copy.json';

export default function App() {
  const [isMatcherOpen, setIsMatcherOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);

  // Synchronize URL routing (/mentor/kian)
  useEffect(() => {
    const handleLocation = () => {
      const path = window.location.pathname;
      if (path === '/mentor/kian') {
        setSelectedMentor('kian');
        setIsNotFound(false);
        trackEvent('mentor_profile_view', { mentor: 'kian' });
      } else if (path === '/' || path === '') {
        setSelectedMentor(null);
        setIsNotFound(false);
      } else {
        setIsNotFound(true);
      }
    };

    handleLocation();
    window.addEventListener('popstate', handleLocation);
    return () => window.removeEventListener('popstate', handleLocation);
  }, []);

  const handleSelectMentor = (_mentorId) => {
    setSelectedMentor('kian');
    window.history.pushState({}, '', `/mentor/kian`);
    trackEvent('mentor_profile_view', { mentor: 'kian' });
  };

  const handleCloseMentorDetail = () => {
    setSelectedMentor(null);
    window.history.pushState({}, '', '/');
  };

  if (isNotFound) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 font-sans transition-colors duration-300">
        <NotFound onReturnHome={() => { setIsNotFound(false); window.history.pushState({}, '', '/'); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 font-sans transition-colors duration-300 selection:bg-amber-500 selection:text-slate-950">
      <SmoothCursor />

      {/* Navigation */}
      <Navbar
        onOpenMatcher={() => { setIsMatcherOpen(true); trackEvent('matcher_start'); }}
        onOpenBooking={() => { setIsBookingOpen(true); trackEvent('hero_booking_click'); }}
      />

      {/* Main Content Container */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* Hero Section with BlurFade Entrance */}
        <BlurFade delay={0.1} inView>
          <Hero
            onOpenBooking={() => { setIsBookingOpen(true); trackEvent('hero_booking_click'); }}
          />
        </BlurFade>

        {/* UVP Bento Grid Section with Scroll BlurFade */}
        <BlurFade delay={0.2} inView>
          <section id="system" className="py-12 border-y border-slate-200 dark:border-slate-800/80 space-y-8">
            <div className="text-center space-y-2">
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-widest">
                The Peer Advantage
              </span>
              <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white">
                Why Relatable Peer Mentors Outperform Tutors
              </h2>
            </div>

            {/* Magic UI Bento Grid (Entire Cards Clickable) */}
            <BentoGrid className="auto-rows-[16rem]">
              <BlurFade delay={0.1} inView>
                <BentoCard
                  name="Low Age Gap Peer Mentors"
                  Icon={Users}
                  className="col-span-3 md:col-span-1"
                  description="Relatable university scholars who recently aced the exact same high school courses and entrance cut-offs."
                  cta="Meet Kian Ahadi"
                  href="#founders"
                />
              </BlurFade>

              <BlurFade delay={0.2} inView>
                <BentoCard
                  name="90%+ Active Recall System"
                  Icon={Brain}
                  className="col-span-3 md:col-span-1"
                  description="Active learning drills and stoichiometric frameworks replacing passive textbook re-reading."
                  cta="Book 15-Min Session"
                  onClick={() => setIsBookingOpen(true)}
                />
              </BlurFade>

              <BlurFade delay={0.3} inView>
                <BentoCard
                  name="OUAC Admission Cut-off Strategy"
                  Icon={Award}
                  className="col-span-3 md:col-span-1"
                  description="Targeting 90%+ averages for competitive programs at McMaster, UofT, Waterloo, and McGill."
                  cta="View Success Tracks"
                  href="#founders"
                />
              </BlurFade>
            </BentoGrid>
          </section>
        </BlurFade>

        {/* Founder Spotlight with Scroll BlurFade */}
        <BlurFade delay={0.2} inView>
          <FounderSpotlight
            onSelectMentor={handleSelectMentor}
            onOpenVideo={(vId) => setActiveVideoId(vId)}
          />
        </BlurFade>

        {/* Specialized Mentors with Scroll BlurFade */}
        <BlurFade delay={0.2} inView>
          <SubjectOfferings />
        </BlurFade>

        {/* Call-First High Converting CTA Banner with ShimmerButton */}
        <BlurFade delay={0.3} inView>
          <section className="my-16 rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-50 via-white to-amber-100 dark:from-amber-500/10 dark:via-slate-900 dark:to-slate-950 p-8 sm:p-12 text-center space-y-6 shadow-xl dark:shadow-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300 text-xs font-semibold uppercase tracking-wider">
              <ShieldCheck className="h-4 w-4" /> Academic Excellence Guarantee
            </div>

            <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
              Ready to Lock Your 90%+ Target Average?
            </h2>

            <p className="mx-auto max-w-2xl text-slate-700 dark:text-slate-300 text-base leading-relaxed">
              {copyData.hero.subtext_rule_formula}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <ShimmerButton
                onClick={() => { trackEvent('hero_call_click'); window.location.href = 'tel:+14374382820'; }}
                shimmerColor="#ffffff"
                background="rgba(234, 179, 8, 1)"
                className="w-full sm:w-auto text-base px-8 py-4"
              >
                <PhoneCall className="h-5 w-5 mr-2" /> Call Kian Directly: +1 (437) 438-2820
              </ShimmerButton>

              <button
                onClick={() => { setIsBookingOpen(true); trackEvent('hero_booking_click'); }}
                className="w-full sm:w-auto px-8 py-4 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-900 font-semibold text-base transition dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-white cursor-pointer"
              >
                Book Strategy Session
              </button>
            </div>
          </section>
        </BlurFade>

      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-12 text-center text-xs text-slate-500 dark:text-slate-400 space-y-2">
        <p>© 2026 GradeLynk. All rights reserved. Peer-to-Peer Academic Mentorship.</p>
        <p>Direct Contact: +1 (437) 438-2820 • Support: kian@gradelynk.com • Academic Director: Kian Ahadi</p>
        <p className="pt-1">
          Designed &amp; Developed by{' '}
          <a
            href="https://www.linkedin.com/in/sadeghizad/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-amber-600 dark:text-amber-400 font-bold hover:text-amber-500 transition"
          >
            mofasahe
          </a>
        </p>
        <p className="pt-2">
          <button onClick={() => setIsPrivacyOpen(true)} className="underline hover:text-amber-500 cursor-pointer">
            Privacy Policy &amp; SMS Terms
          </button>
        </p>
      </footer>

      {/* Modals */}
      <CareerMatcherModal
        isOpen={isMatcherOpen}
        onClose={() => setIsMatcherOpen(false)}
        onSelectMentor={handleSelectMentor}
      />

      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        onOpenPrivacy={() => setIsPrivacyOpen(true)}
      />

      <PrivacyPolicyModal
        isOpen={isPrivacyOpen}
        onClose={() => setIsPrivacyOpen(false)}
      />

      <VideoModal
        isOpen={!!activeVideoId}
        videoId={activeVideoId}
        onClose={() => setActiveVideoId(null)}
      />

      <MentorDetailModal
        isOpen={!!selectedMentor}
        onClose={handleCloseMentorDetail}
        onBookCall={() => setIsBookingOpen(true)}
      />
    </div>
  );
}
